// @flow

import { forEach, mapKeys, pickBy, snakeCase, startCase } from 'lodash/fp';

import { loadScript } from '@zapier/common-utils';
import { getHostedFieldSelector } from '@zapier/payment/src/components/CreditCardForm/HostedFields/utils';

import type {
  HostedFieldAdditionalData,
  HostedFieldProcessorInterface,
  HostedFieldToken,
  HostedFieldType,
  HostedFieldValue,
} from '@zapier/payment/src/types';

class StripeProcessor implements HostedFieldProcessorInterface {
  stripe: any;
  elements: any;

  static instance: ?StripeProcessor = null;
  static elementsRegistry: Map<HostedFieldType, any> = new Map();

  constructor(stripe: any) {
    this.stripe = stripe;
    this.elements = stripe.elements({
      fonts: [
        {
          cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans',
        },
      ],
    });
  }

  createFields(
    style: any,
    onChange: (HostedFieldType, HostedFieldValue) => void
  ) {
    const types = ['number', 'expiry', 'cvc'];

    forEach(type => {
      const element = this.elements.create(`card${startCase(type)}`, {
        style: { base: style },
      });

      StripeProcessor.elementsRegistry.set(type, element);

      element.addEventListener('change', event => {
        onChange(type, {
          isEmpty: event.empty,
          originalError: event.error ? event.error.message : '',
        });
      });

      element.mount(getHostedFieldSelector(type));
    }, types);
  }

  destroyFields() {
    const types = ['number', 'expiry', 'cvc'];

    forEach(type => {
      const element = StripeProcessor.elementsRegistry.get(type);

      if (element) {
        element.destroy();
        StripeProcessor.elementsRegistry.delete(type);
      }
    }, types);
  }

  async createToken(
    additionalData: HostedFieldAdditionalData
  ): Promise<HostedFieldToken> {
    const firstElement = StripeProcessor.elementsRegistry.values().next().value;
    const cleanData = pickBy(Boolean, additionalData);
    const data = mapKeys(snakeCase, cleanData);

    const { error, token } = await this.stripe.createToken(firstElement, data);

    if (error) {
      throw new Error(error.message);
    }

    return {
      token: token.id,
      details: token.card,
    };
  }

  static async create(apiKey: string): Promise<HostedFieldProcessorInterface> {
    if (!StripeProcessor.instance) {
      if (!window.Stripe) {
        await loadScript('https://js.stripe.com/v3/');
      }

      StripeProcessor.instance = new StripeProcessor(window.Stripe(apiKey));
    }

    return StripeProcessor.instance;
  }
}

export default StripeProcessor.create;
